import { useEffect, useState } from 'react';
import { Bell, AlertCircle } from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { notificationPreferencesApi, ApiError } from '@solara/api-client';
import type { NotificationPreferenceDto } from '@solara/api-client';

const MODES: Array<NotificationPreferenceDto['mode']> = ['IMMEDIATE', 'DAILY_DIGEST', 'WEEKLY_DIGEST'];

export default function NotificationPrefsPage() {
  const { t } = useTranslation('p1');
  const catLabel = (c: string) =>
    t(`notificationPrefs.categories.${c}`, { defaultValue: c });
  const modeLabel = (m: string) =>
    t(`notificationPrefs.modes.${m}`, { defaultValue: m.replace(/_/g, ' ') });
  const [prefs, setPrefs] = useState<NotificationPreferenceDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      setPrefs(await notificationPreferencesApi.list());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  async function toggle(category: string, patch: Partial<NotificationPreferenceDto>) {
    try {
      const updated = await notificationPreferencesApi.update(category, patch);
      setPrefs((current) => current.map((p) => (p.category === category ? updated : p)));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    }
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('notificationPrefs.title')}</h1>
        <p className="text-fg-muted text-sm">{t('notificationPrefs.subtitle')}</p>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            {t('notificationPrefs.categoriesTitle')}
          </CardTitle>
        </CardHeader>
        <CardBody>
          <table className="w-full text-sm">
            <thead className="text-xs text-fg-muted">
              <tr className="text-start">
                <th className="py-2">{t('notificationPrefs.category')}</th>
                <th className="py-2 text-center">{t('notificationPrefs.email')}</th>
                <th className="py-2 text-center">{t('notificationPrefs.sms')}</th>
                <th className="py-2 text-center">{t('notificationPrefs.push')}</th>
                <th className="py-2 text-center">{t('notificationPrefs.inApp')}</th>
                <th className="py-2">{t('notificationPrefs.mode')}</th>
                <th className="py-2">{t('notificationPrefs.dnd')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {prefs.map((p) => (
                <tr key={p.category}>
                  <td className="py-2 font-medium">{catLabel(p.category)}</td>
                  {(['channelEmail', 'channelSms', 'channelPush', 'channelInApp'] as const).map((field) => (
                    <td key={field} className="py-2 text-center">
                      <input
                        type="checkbox"
                        checked={p[field]}
                        onChange={(e) => void toggle(p.category, { [field]: e.target.checked })}
                      />
                    </td>
                  ))}
                  <td className="py-2">
                    <select
                      className="rounded border border-border bg-surface px-2 py-1 text-xs"
                      value={p.mode}
                      onChange={(e) => void toggle(p.category, { mode: e.target.value as NotificationPreferenceDto['mode'] })}
                    >
                      {MODES.map((m) => (
                        <option key={m} value={m}>
                          {modeLabel(m)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2">
                    <div className="flex items-center gap-1 text-xs">
                      <Input
                        type="time"
                        value={p.dndStart ?? ''}
                        onChange={(e) => void toggle(p.category, { dndStart: e.target.value || null })}
                        className="w-24"
                      />
                      –
                      <Input
                        type="time"
                        value={p.dndEnd ?? ''}
                        onChange={(e) => void toggle(p.category, { dndEnd: e.target.value || null })}
                        className="w-24"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </div>
  );
}
